import { Button, Input, MediaCard, Modal } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { EVENTS, publish } from '@sendible/event-manager';
import { Media } from '@sendible/shared-state-bridge';
import { useYouTubeService } from '@sendible/frontendv2/src/utils/googleAuthentication/useYouTubeService';
import { GoogleService, type YouTubeVideo } from '@sendible/frontendv2/src/utils/googleAuthentication/types';
import { ConnectService, Container, Footer, GridContent, Header } from '../styles/index.styles';
import { LoadingState } from '../LoadingState/index';
import { BlankState } from '../BlankState/index';
import { useComposeBoxContext } from '../../../composeBoxContext';
import { MediaPreview } from '../../../..';

export const YoutubeModal = () => {
  const { setIsYoutubeModalOpen } = useComposeBoxContext();
  const { isAuthenticated, isLoading: authLoading, authorizeYouTube, logout, searchVideos } = useYouTubeService();
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [showPreview, setShowPreview] = useState(false);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const { t } = useTranslation(['common_terms', 'compose_box']);

  // Convert YouTube videos to Media format for preview
  const mediaList: Media[] = videos.map((video) => ({
    url: `https://www.youtube.com/watch?v=${video.id}`,
    type: 'video' as Media['type'],
    id: video.id,
    isGif: false,
    name: video.title,
    downloadUrl: '',
  }));

  const fetchVideos = useCallback(
    async (query = '', pageToken = '') => {
      if (!isAuthenticated) return null;

      return searchVideos({
        query: query.trim() || undefined,
        maxResults: 20,
        pageToken: pageToken || undefined,
      });
    },
    [isAuthenticated, searchVideos]
  );

  const loadVideos = useCallback(
    async (query = '', pageToken = '') => {
      if (!isAuthenticated) return;

      setIsLoading(true);
      setError(undefined);

      try {
        const response = await fetchVideos(query, pageToken);

        if (!response) return;

        setVideos(response.videos);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load videos');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchVideos, isAuthenticated]
  );

  useEffect(() => {
    if (!isAuthenticated) return;

    let isActive = true;

    const loadInitialVideos = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const response = await fetchVideos();

        if (!isActive || !response) return;

        setVideos(response.videos);
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err.message : 'Failed to load videos');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadInitialVideos();

    return () => {
      isActive = false;
    };
  }, [fetchVideos, isAuthenticated]);

  const handleAuthorizeYouTube = async () => {
    await authorizeYouTube();
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = () => {
    if (isAuthenticated && searchQuery.trim()) {
      loadVideos(searchQuery);
    }
  };

  const handleKeyPress = (key: string) => {
    if (key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleMediaSelection = (mediaId: string) => {
    if (selectedItem === mediaId) {
      setSelectedItem(undefined);
    } else {
      setSelectedItem(mediaId);
    }
  };

  const handleVideoPreview = (videoId: string) => {
    const videoIndex = videos.findIndex((video) => video.id === videoId);

    if (videoIndex !== -1) {
      setPreviewInitialItem(videoIndex);
      setShowPreview(true);
    }
  };

  const handleImportClick = () => {
    if (selectedItem) {
      const selectedVideo = videos.find((video) => video.id === selectedItem);

      if (selectedVideo) {
        const youtubeLink = `https://www.youtube.com/watch?v=${selectedVideo.id}`;

        publish(EVENTS.IMPORT_YT_LINK, youtubeLink);
        setIsYoutubeModalOpen(false);
      }
    }
  };

  const handleChangeAccount = () => {
    logout(GoogleService.YouTube);
    setVideos([]);
    setSelectedItem(undefined);
  };

  const subheaderContent = (
    <Header>
      <Input
        icon="search"
        size="sm"
        label={t('compose_box:search_videos') as string}
        change={handleSearch}
        value={searchQuery}
        keyPress={handleKeyPress}
      />
    </Header>
  );

  return (
    <>
      <Modal
        portalDomId="layer-root"
        close={() => setIsYoutubeModalOpen(false)}
        preventEscapeClose={showPreview}
        footerContent={
          isAuthenticated ? (
            <Footer>
              <Button
                appearance="primary"
                disabled={!selectedItem}
                label={t('import_link', { ns: 'compose_box' }) as string}
                onClick={handleImportClick}
                size={14}
                variation="fill"
              />
            </Footer>
          ) : undefined
        }
        headingAction={
          isAuthenticated ? (
            <Button
              appearance="primary"
              label={t('change_account') as string}
              onClick={handleChangeAccount}
            />
          ) : undefined
        }
        headingIcon="youtube_color"
        headingTitle={t('youtube') as string}
        subHeaderContent={isAuthenticated && subheaderContent}
        variation="large"
      >
        <Container>
          {!isAuthenticated ? (
            <ConnectService>
              <Button
                icon="google_color"
                label={t('continue_with_google') as string}
                onClick={handleAuthorizeYouTube}
                size={16}
                variation="outline"
                isLoading={authLoading}
              />
            </ConnectService>
          ) : error ? (
            <BlankState />
          ) : !isLoading && videos.length === 0 ? (
            <BlankState />
          ) : (
            <GridContent>
              {isLoading ? (
                <LoadingState />
              ) : (
                videos.map((video) => (
                  <MediaCard
                    duration={video.duration}
                    id={video.id}
                    key={video.id}
                    name={video.title}
                    open={() => handleVideoPreview(video.id)}
                    select={() => handleMediaSelection(video.id)}
                    thumbUrl={video.thumbnailUrl}
                    type="Video"
                    updateDate={video.viewCount ? `${video.viewCount} views` : video.publishedAt}
                    uploadingText={t('loading') as string}
                    isSelected={selectedItem === video.id}
                  />
                ))
              )}
            </GridContent>
          )}
        </Container>
      </Modal>
      {showPreview && mediaList.length > 0 && (
        <MediaPreview
          initialMediaIndex={previewInitialItem}
          mediaList={mediaList}
          onClose={() => setShowPreview(false)}
          shouldShowActions={false}
        />
      )}
    </>
  );
};
